import clsx from 'clsx';
import { cloneElement, forwardRef, isValidElement, type ReactElement, type ReactNode } from 'react';
import styles from './button.module.less';
import { logger } from '@/lib/utils/logger';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  label?: string;
  fullWidth?: boolean;
}

const Spinner = () => (
  <svg
    className={styles.spinner}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className={styles.spinnerCircle}
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className={styles.spinnerPath}
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      asChild = false,
      loading = false,
      disabled,
      iconLeft,
      iconRight,
      label,
      fullWidth = false,
      children,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const classes = clsx(
      styles.button,
      styles[`variant-${variant}`],
      styles[`size-${size}`],
      loading && styles.loading,
      fullWidth && styles.fullWidth,
      className
    );

    const shouldRenderDefaultContent =
      iconLeft !== undefined || iconRight !== undefined || label !== undefined || loading;

    let contentChildren: ReactNode = children;
    let childElement: ReactElement | null = null;

    if (asChild) {
      if (!isValidElement(children)) {
        if (process.env.NODE_ENV !== 'production') {
          logger.error('Button', new Error('Button with asChild requires a single valid element.'));
        }
        return null;
      }
      childElement = children as ReactElement;
      const childProps = childElement.props as Record<string, unknown>;
      contentChildren = childProps.children as ReactNode;
    }

    const content = shouldRenderDefaultContent ? (
      <span className={styles.content}>
        {loading && <Spinner />}
        {iconLeft && <span className={styles.icon}>{iconLeft}</span>}
        {label && <span className={styles.label}>{label}</span>}
        {iconRight && <span className={styles.icon}>{iconRight}</span>}
      </span>
    ) : (
      <>
        {loading && <Spinner />}
        {contentChildren}
      </>
    );

    if (asChild && childElement) {
      const childProps = childElement.props as Record<string, unknown>;
      const cloneProps = {
        className: clsx(childProps.className as string | undefined, classes),
        ...props,
        children: content,
      } as Parameters<typeof cloneElement>[1] & { ref?: React.Ref<unknown> };

      if (ref) {
        cloneProps.ref = ref;
      }

      return cloneElement(childElement, cloneProps);
    }

    return (
      <button
        className={classes}
        ref={ref as React.Ref<HTMLButtonElement>}
        disabled={disabled || loading}
        // UIUX-27: disabled+spinner alone only announces "dimmed"; aria-busy
        // tells AT the button is working, not broken.
        aria-busy={loading || undefined}
        type={type}
        {...props}
      >
        {content}
      </button>
    );
  }
);
Button.displayName = 'Button';
